import React, { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";

import { LearnerPractitionerCardPartsFragment } from "../api/types";

import { useTranslation } from "lib/i18n/use-translation";
import { useInViews } from "utility/useInViews";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import { trackEvent } from "utility/mixpanel";

import styles from "./practitioner-card.module.scss";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";

interface PractitionerCardProps {
  practitioner: LearnerPractitionerCardPartsFragment;
  listName?: string;
  listPosition?: number;
}

const PractitionerCard = ({
  practitioner: { id, photoUrl, fullName },
  listName,
  listPosition,
}: PractitionerCardProps) => {
  const { ref, inView } = useInViews();
  const { t } = useTranslation(["components"]);

  const userID = localStorage.getItem("userID");
  const companyName = localStorage.getItem("companyName") || "NA";

  const logImpression = useCallback(() => {
    logAnalyticsEvent("practitioner_impression", {
      user_id: userID,
      user_id_wone: userID,
      user_company_name: companyName,
      practitioner_id: id,
      practitioner_full_name: fullName,
      list_name: listName,
      list_position: listPosition,
      page_title: document.title,
      page_path: window.location.pathname,
    });

    trackEvent("Practitioner Card Impression", {
      "Practitioner ID": id,
      "Practitioner Name": fullName,
      "List Name": listName,
      "List Position": listPosition,
    });
  }, [id, fullName, listName, listPosition, userID, companyName]);

  const logClick = useCallback(() => {
    logAnalyticsEvent("practitioner_click", {
      user_id: userID,
      user_id_wone: userID,
      user_company_name: companyName,
      practitioner_id: id,
      practitioner_full_name: fullName,
      list_name: listName,
      list_position: listPosition,
      page_title: document.title,
      page_path: window.location.pathname,
    });

    trackEvent("Practitioner Card Clicked", {
      "Practitioner ID": id,
      "Practitioner Name": fullName,
      "List Name": listName,
      "List Position": listPosition,
    });
  }, [id, fullName, listName, listPosition, userID, companyName]);

  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  return (
    <Link
      ref={ref}
      to={`/user/practitioners/${id}`}
      onClick={logClick}
      state={{
        listName,
        listPosition,
        pageTitlePrefix: fullName,
        canGoBack: true,
      }}
      className={styles["link"]}
    >
      <div className={styles["practitioner-card-container"]} ref={ref}>
        <div className={styles["practitioner-card-content"]}>
          <div className={styles["image-container"]}>
            <img src={photoUrl} alt="expert-pic" />
          </div>
          <div className={styles["detail"]}>
            <p className={styles["title"]}>
              {t("components:practitioner.hostedBy")}
            </p>
            <p className={styles["subtitle"]}>{fullName}</p>
          </div>
        </div>
        <div className={styles["icon-container"]}>
          <ChevronIcon aria-hidden="true" focusable="false" />
        </div>
      </div>
    </Link>
  );
};

export default PractitionerCard;
